import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { google as googleModel } from '@shared/models/googlemaps.model';
declare const google: any;
import DirectionsResult = googleModel.maps.DirectionsResult;
import DirectionsStatus = googleModel.maps.DirectionsStatus;
import { SetDriveDirection, SetDriveDirectionResult } from '@store/search-board/search-board.actions';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import Autocomplete = googleModel.maps.places.Autocomplete;
import { selectDriveDirection, selectSidePanelState } from '@store/search-board/search-board.selector';
import { filter } from 'rxjs/operators';
import { DriveDirection, SidePanelTab, SidePanelView } from '@store/search-board/search-board.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';
import { MixpanelService } from '@shared/services/mixpanel.service';

@UntilDestroy()
@Component({
  selector: 'rlst-driving-directions',
  templateUrl: './driving-directions.component.html',
  styleUrls: ['./driving-directions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DrivingDirectionsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() closeDriveDirection: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('from', {static: true}) from: ElementRef;
  @ViewChild('to', {static: true}) to: ElementRef;

  public drivingForm: UntypedFormGroup;
  public errorMessage: string = '';
  public readonly MAX_LENGTH = 100;
  private autoCompleteManagerFromField: Autocomplete;
  private autoCompleteManagerToField: Autocomplete;

  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);

  constructor(private readonly fb: UntypedFormBuilder,
              private readonly cdr: ChangeDetectorRef,
              private readonly store$: Store<AppState>,
              private readonly mixpanelService: MixpanelService) { }

  ngOnInit(): void {
    this.createDrivingForm();
    this.store$.pipe(
      select(selectDriveDirection),
      filter(Boolean),
      untilDestroyed(this),
    ).subscribe((driveDirection: DriveDirection) => {
      if (driveDirection.from === '' && driveDirection.to === '') {
        this.errorMessage = '';
        this.drivingForm.reset();
      } else {
        this.drivingForm.setValue(driveDirection);
      }

      this.cdr.markForCheck();
    });

    this.store$
      .pipe(
        select(selectSidePanelState),
        untilDestroyed(this)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });
  }

  ngAfterViewInit(): void {
    this.autoCompleteManagerFromField = new google.maps.places.Autocomplete(this.from.nativeElement);
    this.autoCompleteManagerFromField.setComponentRestrictions({'country': ['us']});
    this.autoCompleteManagerFromField.addListener('place_changed', () => {
      this.store$.dispatch(
        new SetDriveDirection({
          ...this.drivingForm.value,
          from: this.from.nativeElement.value.slice(0, this.MAX_LENGTH)
        })
      );
    });
    this.autoCompleteManagerToField = new google.maps.places.Autocomplete(this.to.nativeElement);
    this.autoCompleteManagerToField.setComponentRestrictions({'country': ['us']});
    this.autoCompleteManagerToField.addListener('place_changed', () => {
      this.store$.dispatch(
        new SetDriveDirection({
          ...this.drivingForm.value,
          to: this.to.nativeElement.value.slice(0, this.MAX_LENGTH)
        })
      );
    });
  }

  public getDriveDirections(): void {
    this.store$.dispatch(new SetDriveDirection(this.drivingForm.value));
    const directionsService = new google.maps.DirectionsService();

    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track('Search - Driving Directions Added', {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords
    });

    directionsService.route({
      origin: this.drivingForm.controls.from.value,
      destination: this.drivingForm.controls.to.value,
      travelMode: google.maps.DirectionsTravelMode.DRIVING
    }, (response: DirectionsResult, status: DirectionsStatus) => {
      if (status === google.maps.DirectionsStatus.OK) {
        this.store$.dispatch(new SetDriveDirectionResult(response));
        this.errorMessage = '';
        this.closeDriveDirection.emit();
      } else {
        if (status === google.maps.DirectionsStatus.NOT_FOUND) {
          this.errorMessage = 'Direction not found';
        } else
        if (status === google.maps.DirectionsStatus.ZERO_RESULTS) {
          this.errorMessage = 'No route found';
        } else
        if (status === google.maps.DirectionsStatus.MAX_ROUTE_LENGTH_EXCEEDED) {
          this.errorMessage = 'Requested route is too long';
        } else
        if (status === google.maps.DirectionsStatus.OVER_DAILY_LIMIT ||
            status === google.maps.DirectionsStatus.OVER_QUERY_LIMIT) {
          this.errorMessage = 'Over daily limit';
        } else {
          this.errorMessage = 'Cannot be processed';
        }
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.autoCompleteManagerFromField.unbindAll();
    this.autoCompleteManagerToField.unbindAll();
  }

  private createDrivingForm(): void {
    this.drivingForm = this.fb.group({
      from: ['', [Validators.required]],
      to: ['', [Validators.required]]
    });
  }
}
